/**
 * ProductsApiClient
 * 
 * Handles product management, categories, and e-commerce operations
 */

import { BaseApiClient } from './BaseApiClient';
import type { Product, ProductCategory, ProductFilters, ProductStats, ProductImportJob, ProductImage } from '../../types/api/merchant';

export class ProductsApiClient extends BaseApiClient {
  /**
   * Get products with filtering and pagination
   */
  async getProducts(filters?: ProductFilters & { page?: number; limit?: number }): Promise<{
    products: Product[];
    total: number;
    page: number;
    totalPages: number;
  }> {
    const params = new URLSearchParams();
    if (filters?.page) params.append('page', filters.page.toString());
    if (filters?.limit) params.append('limit', filters.limit.toString());
    if (filters?.categoryId) params.append('categoryId', filters.categoryId);
    if (filters?.status) params.append('status', filters.status);
    if (filters?.inStock !== undefined) params.append('inStock', filters.inStock.toString());
    if (filters?.search) params.append('search', filters.search);
    if (filters?.priceMin) params.append('priceMin', filters.priceMin.toString());
    if (filters?.priceMax) params.append('priceMax', filters.priceMax.toString());
    if (filters?.sortBy) params.append('sortBy', filters.sortBy);
    if (filters?.sortOrder) params.append('sortOrder', filters.sortOrder);
    
    return this.get(`/merchant/products?${params}`);
  }

  /**
   * Get single product
   */
  async getProduct(productId: string): Promise<Product> {
    const result = await this.get<{ product: Product }>(`/merchant/products/${productId}`);
    return result.product;
  }

  /**
   * Create new product
   */
  async createProduct(productData: Omit<Product, 'id' | 'merchantId' | 'salesCount' | 'totalRevenue' | 'createdAt' | 'updatedAt' | 'qrCode' | 'paymentLink'>): Promise<Product> {
    const result = await this.post<{ product: Product }>('/merchant/products', productData);
    return result.product;
  }

  /**
   * Update product
   */
  async updateProduct(productId: string, updates: Partial<Product>): Promise<Product> {
    const result = await this.put<{ product: Product }>(`/merchant/products/${productId}`, updates);
    return result.product;
  }

  /**
   * Delete product
   */
  async deleteProduct(productId: string): Promise<void> {
    await this.delete(`/merchant/products/${productId}`);
  }

  /**
   * Generate product QR code
   */
  async generateProductQR(productId: string): Promise<{ qrCode: string; paymentLink: string }> {
    return this.post(`/merchant/products/${productId}/qr-code`);
  }

  /**
   * Update product inventory
   */
  async updateProductInventory(productId: string, inventoryData: {
    quantity?: number;
    trackQuantity?: boolean;
    lowStockThreshold?: number;
  }): Promise<Product> {
    const result = await this.put<{ product: Product }>(`/merchant/products/${productId}/inventory`, inventoryData);
    return result.product;
  }

  /**
   * Upload product image
   */
  async uploadProductImage(productId: string, file: File, isPrimary?: boolean): Promise<ProductImage> {
    const formData = new FormData();
    formData.append('image', file);
    if (isPrimary !== undefined) formData.append('isPrimary', isPrimary.toString());

    const response = await fetch(`${this.baseURL}/merchant/products/${productId}/images`, {
      method: 'POST',
      headers: {
        ...(this.token && { Authorization: `Bearer ${this.token}` }),
      },
      body: formData,
    });

    if (!response.ok) {
      throw new Error('Failed to upload product image');
    }

    const result = await response.json();
    return result.image;
  }

  /**
   * Delete product image
   */
  async deleteProductImage(productId: string, imageId: string): Promise<void> {
    await this.delete(`/merchant/products/${productId}/images/${imageId}`);
  }

  /**
   * Reorder product images
   */
  async reorderProductImages(productId: string, imageOrder: Array<{ id: string; order: number }>): Promise<void> {
    await this.put(`/merchant/products/${productId}/images/reorder`, { imageOrder });
  }

  // Product Categories
  /**
   * Get product categories
   */
  async getProductCategories(): Promise<ProductCategory[]> {
    const result = await this.get<{ categories: ProductCategory[] }>('/merchant/categories');
    return result.categories;
  }

  /**
   * Create product category
   */
  async createProductCategory(categoryData: Omit<ProductCategory, 'id' | 'productCount' | 'createdAt'>): Promise<ProductCategory> {
    const result = await this.post<{ category: ProductCategory }>('/merchant/categories', categoryData);
    return result.category;
  }

  /**
   * Update product category
   */
  async updateProductCategory(categoryId: string, updates: Partial<ProductCategory>): Promise<ProductCategory> {
    const result = await this.put<{ category: ProductCategory }>(`/merchant/categories/${categoryId}`, updates);
    return result.category;
  }

  /**
   * Delete product category
   */
  async deleteProductCategory(categoryId: string): Promise<void> {
    await this.delete(`/merchant/categories/${categoryId}`);
  }

  // Product Analytics
  /**
   * Get product statistics
   */
  async getProductStats(): Promise<ProductStats> {
    return this.get('/merchant/products/stats');
  }

  /**
   * Get product analytics
   */
  async getProductAnalytics(productId: string, period?: '7d' | '30d' | '90d' | '1y'): Promise<{
    salesHistory: Array<{ date: string; sales: number; revenue: number }>;
    viewCount: number;
    conversionRate: number;
    avgOrderValue: number;
  }> {
    const params = new URLSearchParams();
    if (period) params.append('period', period);
    
    return this.get(`/merchant/products/${productId}/analytics?${params}`);
  }

  // Bulk Operations
  /**
   * Bulk update products
   */
  async bulkUpdateProducts(updates: Array<{ id: string; data: Partial<Product> }>): Promise<void> {
    await this.put('/merchant/products/bulk-update', { updates });
  }

  /**
   * Bulk delete products
   */
  async bulkDeleteProducts(productIds: string[]): Promise<void> {
    await this.delete('/merchant/products/bulk-delete', { 
      body: JSON.stringify({ productIds }) 
    });
  }

  // Product Import (Google Product Feed)
  /**
   * Upload product feed
   */
  async uploadProductFeed(file: File): Promise<ProductImportJob> {
    const formData = new FormData();
    formData.append('feed', file);

    const response = await fetch(`${this.baseURL}/merchant/products/import/upload`, {
      method: 'POST',
      headers: {
        ...(this.token && { Authorization: `Bearer ${this.token}` }),
      },
      body: formData,
    });

    if (!response.ok) {
      throw new Error('Failed to upload product feed');
    }

    const result = await response.json();
    return result.job;
  }

  /**
   * Get import job status
   */
  async getImportJobStatus(jobId: string): Promise<ProductImportJob> {
    const result = await this.get<{ job: ProductImportJob }>(`/merchant/products/import/${jobId}`);
    return result.job;
  }

  /**
   * Cancel import job
   */
  async cancelImportJob(jobId: string): Promise<void> {
    await this.post(`/merchant/products/import/${jobId}/cancel`);
  }

  /**
   * Get import history
   */
  async getImportHistory(page = 1, limit = 10): Promise<{
    jobs: ProductImportJob[];
    total: number;
    page: number;
    totalPages: number;
  }> {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString()
    });
    
    return this.get(`/merchant/products/import/history?${params}`);
  }

  /**
   * Retry import job
   */
  async retryImportJob(jobId: string): Promise<ProductImportJob> {
    const result = await this.post<{ job: ProductImportJob }>(`/merchant/products/import/${jobId}/retry`);
    return result.job;
  }

  // Product Search and Discovery
  /**
   * Search products
   */
  async searchProducts(query: string, filters?: ProductFilters): Promise<{
    products: Product[];
    total: number;
    suggestions: string[];
  }> {
    const params = new URLSearchParams({ q: query });
    if (filters?.categoryId) params.append('categoryId', filters.categoryId);
    if (filters?.priceMin) params.append('priceMin', filters.priceMin.toString());
    if (filters?.priceMax) params.append('priceMax', filters.priceMax.toString());
    
    return this.get(`/merchant/products/search?${params}`);
  }

  /**
   * Get product recommendations
   */
  async getProductRecommendations(productId: string, limit: number = 5): Promise<Product[]> {
    const result = await this.get<{ products: Product[] }>(`/merchant/products/${productId}/recommendations?limit=${limit}`);
    return result.products;
  }

  /**
   * Get trending products
   */
  async getTrendingProducts(period: '24h' | '7d' | '30d' = '7d', limit: number = 10): Promise<Product[]> {
    const result = await this.get<{ products: Product[] }>(`/merchant/products/trending?period=${period}&limit=${limit}`);
    return result.products;
  }

  /**
   * Get low stock products
   */
  async getLowStockProducts(): Promise<Product[]> {
    const result = await this.get<{ products: Product[] }>('/merchant/products/low-stock');
    return result.products;
  }

  /**
   * Get out of stock products
   */
  async getOutOfStockProducts(): Promise<Product[]> {
    const result = await this.get<{ products: Product[] }>('/merchant/products/out-of-stock');
    return result.products;
  }
} 